import { fetchUtils } from 'react-admin';
import simpleRestProvider from 'ra-data-simple-rest';
import { baseUrl } from './consts';

// HTTP client configuration
const httpClient = (url, options = {}) => {
    options.headers = options.headers || new Headers({ Accept: 'application/json' });

    const token = localStorage.getItem('auth_token');
    if (token) {
        options.headers.set('Authorization', `Bearer ${token}`);
    }

    return fetchUtils.fetchJson(url, options);
};

// Initialize the base data provider
const dataProvider = simpleRestProvider(baseUrl + '/v1/manager', httpClient);

// Override 'create' method
dataProvider.create = async (resource, params) => {
    let url, options;

    if (resource === 'notifications') {
        url = baseUrl + '/v1/manager/notifications';
        options = {
            method: 'POST',
            body: JSON.stringify({
                title: params.data.title,
                message: params.data.message,
            }),
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            }),
        };
    } else if (resource === 'popup-ad') {
        const formData = new FormData();
        formData.append('popupAd', params.data.file);

        url = baseUrl + '/v1/manager/popup-ad';
        options = {
            method: 'POST',
            body: formData,
        };
    } else if (resource === 'slider') {
        const formData = new FormData();
        formData.append('slider', params.data.file);
        formData.append('asset_name', params.data.asset_name); 
        formData.append('URL', params.data.URL);               
        formData.append('asset_type', 'slider_image');   
        
        for (let [key, value] of formData.entries()) {
            console.log(`${key}: ${value}`);
        }

        url = baseUrl + '/v1/manager/sliders';
        options = {
            method: 'POST',
            body: formData,
        };
    } else if (resource === 'service-groups') {
        url = baseUrl + '/v1/manager/service-groups';
        options = {
            method: 'POST',
            body: JSON.stringify(params.data),
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            }),
        };
        const response = await httpClient(url, options);
        return { data: { ...params.data, id: response.json.group_id } };
    } else if (resource === 'package-groups') {
        url = baseUrl + '/v1/manager/package-groups';
        options = {
            method: 'POST',
            body: JSON.stringify(params.data),
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            }),
        };
        const response = await httpClient(url, options);
        return { data: { ...params.data, id: response.json.id || Date.now() } };
    } else {
        return simpleRestProvider(baseUrl + '/v1/manager', httpClient).create(resource, params);
    }

    return httpClient(url, options).then(({ json }) => ({
        data: { ...params.data, id: json.id || params.data.srvid || Date.now() },
    }));
};

// Override 'getList'
dataProvider.getList = async (resource, params) => {
    let url;

    if (resource === 'notifications') {
        url = baseUrl + '/v1/manager/notifications';
    } else if (resource === 'popup-ad') {
        url = baseUrl + '/v1/manager/popup-ad';
    } else if (resource === 'slider') {
        url = baseUrl + '/v1/manager/sliders';
    } else if (resource === 'service-groups') {
        url = baseUrl + '/v1/manager/service-groups';
    } else if (resource === 'package-groups') { 
        url = baseUrl + '/v1/manager/package-groups';
    } else if (resource === 'all-packages') {
        // MATCHING YOUR GO ROUTE: /v1/shared/all-packages
        url = baseUrl + '/v1/shared/all-packages';
    } else {
        return simpleRestProvider(baseUrl + '/v1/manager', httpClient).getList(resource, params);
    }

    return httpClient(url).then(({ json }) => {
        const data = json.packages ? json.packages : (Array.isArray(json) ? json : [json]);
        return {
            data: data.map((item, index) => ({
                id: item?.group_id || item?.srvid || item?.package_id || item?.id || index + 1,
                ...item,
            })),
            total: data.length,
        };
    });
};

// Override 'delete' method
dataProvider.delete = async (resource, params) => {
    let url;

    if (resource === 'notifications') {
        url = `${baseUrl}/v1/manager/notifications/${params.id}`;
    } else if (resource === 'popup-ad') {
        url = baseUrl + '/v1/manager/popup-ad';
    } else if (resource === 'slider') {
        url = `${baseUrl}/v1/manager/sliders/${params.asset_name}`;
    } else if (resource === 'service-groups') {
        url = `${baseUrl}/v1/manager/service-groups/${params.id}`;
    } else if (resource === 'package-groups') {
        url = `${baseUrl}/v1/manager/package-groups/${params.id}`;
    } else if (resource === 'services') {
        url = `${baseUrl}/v1/manager/services/${params.id}`;
    } else {
        return simpleRestProvider(baseUrl + '/v1/manager', httpClient).delete(resource, params);
    }

    const options = {
        method: 'DELETE',
        headers: new Headers({
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        }),
    };

    const response = await fetch(url, options);
    if (!response.ok) throw new Error('Failed to delete');
    return { data: { id: params.id } };
};

// Override 'getOne'
dataProvider.getOne = async (resource, params) => {
    if (resource === 'service-groups') {
        const url = `${baseUrl}/v1/manager/service-groups/${params.id}`;
        const response = await httpClient(url);
        return { data: { id: response.json.group_id, ...response.json } };
    }
    return simpleRestProvider(baseUrl + '/v1/manager', httpClient).getOne(resource, params);
};

// Override 'update'
dataProvider.update = async (resource, params) => {
    if (resource === 'service-groups') {
        const url = `${baseUrl}/v1/manager/service-groups/${params.id}`;
        const options = {
            method: 'PUT',
            body: JSON.stringify(params.data),
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            }),
        };
        const response = await httpClient(url, options);
        return { data: { ...params.data, id: response.json.group_id } };
    } 
    
    if (resource === 'package-groups') {
        const url = `${baseUrl}/v1/manager/package-groups/${params.id}`;
        const options = {
            method: 'PUT',
            body: JSON.stringify(params.data),
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            }),
        };
        const response = await httpClient(url, options);
        return { data: { ...params.data, id: response.json.id || params.id } };
    }

    return simpleRestProvider(baseUrl + '/v1/manager', httpClient).update(resource, params);
};

export default dataProvider;